import { useCallback, useEffect, useState } from "react";
import { Footer, Header } from "react-corner-ds";
import desktopLogo from "./assets/img/logos/corner-logo.svg";
import mobileLogo from "./assets/img/logos/corner-logo-mobile.svg";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import GenericError from "./components/screens/errors/genericError/GenericError";
import { useApi } from "./hooks/useApi/useApi";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import { setFader } from "./redux/states/fader/faderSlice";
import { useTranslation } from "react-i18next";
import ManualNavigation from "./components/utils/manualNavigation/ManualNavigation";
import {
  OnboardingFullSteps,
  OnboardingLightSteps,
  pagesWithBackButton,
  CrossSellingPagesWithBackButton,
  pagesWithProgressBarFull,
  pagesWithProgressBarLight,
} from "./utils/pagesConfig";
import PersonalData from "./components/screens/personalData/PersonalData";
import PersonalDataAdditional from "./components/screens/personalDataAdditional/PersonalDataAdditional";
import Address from "./components/screens/address/Address";
import EmploymentDetails from "./components/screens/employmentDetails/EmploymentDetails";
import InformationRecap from "./components/screens/informationRecap/InformationRecap";
import { useFlow } from "./hooks/useFlow/useFlow";
import { Request } from "../src/models/request/request.model";
import { store } from "./redux/store";
import { useAdobe } from "./hooks/useAdobe/useAdobe";
import style from "./App.module.css";
import { setQueryParams } from "./redux/states/queryParams/queryParamsSlice";
import { QueryParams } from "./models/queryParams/queryParams.model";
import { checkParamValidity, definePatternByName } from "./utils/constants";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ContractView from "./components/screens/contractView/ContractView";
import VideoIdentification from "./components/screens/videoIdentification/VideoIdentification";
import SuccessVideoId from "./components/screens/successes/successVideoId/SuccessVideoId";
import FailureVideoId from "./components/screens/errors/failureVideoId/FailureVideoId";
import VideoIdentificationUnavailable from "./components/screens/videoIdentificationUnavailable/VideoIdentificationUnavailable";
import { useCustomNavigation } from "./hooks/useCustomNavigation/useCustomNavigation";
import MissingData from "./components/screens/errors/missingData/MissingData";

function App() {
  const { t, i18n } = useTranslation();
  const [isFaded, setIsFaded] = useState<boolean>(true);
  const [appReady, setAppReady] = useState<boolean>(false);
  const [isDisableBackButton, setIsDisableBackButton] =
    useState<boolean>(false);
  const [isOnboardingLight, setIsOnboardingLight] = useState<boolean>(false);

  const [pagesWithBack, setPagesWithBack] =
    useState<string[]>(pagesWithBackButton);

  const appConf = useAppSelector((state) => state.appConf);

  const fader = useAppSelector((state) => state.fader);
  const reduxDispatch = useAppDispatch();

  const [searchParams] = useSearchParams();

  const { loadConfiguration, appStartup, callSteploader } = useFlow();

  const { startAxiosResponseInterceptor } = useApi();

  const { firePageLoad } = useAdobe();

  let { customNavigation } = useCustomNavigation();

  const width = window.innerWidth;

  useEffect(() => {
    setIsFaded(fader.fading);
  }, [fader]);

  useEffect(() => {
    if (appReady) {
      firePageLoad();
    }
  }, [appReady, firePageLoad]);

  const configurationAndStartup = useCallback(async () => {
    // Get query params
    const langParam = searchParams.get("lang");
    const flow = searchParams.get("flow");
    const resumeToken = searchParams.get("resumeToken");
    const crossSellingParameter = searchParams.get("csp");
    const acquisitionpage = searchParams.get("acquisitionpage");
    const intcmp = searchParams.get("intcmp");
    const medium = searchParams.get("medium");
    const cid = searchParams.get("cid");
    const campaigntype = searchParams.get("campaigntype");
    let channelParam = searchParams.get("channel");
    const videoIdParam = searchParams.get("videoid");
    const wave = searchParams.get("wave");
    const campaignCode = searchParams.get("campaigncode");

    //Update channelParam based on acquisition page
    channelParam =
      channelParam !== null
        ? channelParam
        : acquisitionpage &&
          ["iCornerApp", "MyOnlineServicesApp", "card24App"].includes(
            acquisitionpage
          )
        ? "App"
        : "Web";

    // Store query params
    let queryParams: QueryParams = {
      langParam: langParam ? langParam : "",
      flow: flow ? flow : "",
      resumeToken: resumeToken ? resumeToken : "",
      crossSellingParameter: crossSellingParameter ? crossSellingParameter : "",
      acquisitionpage: acquisitionpage ? acquisitionpage : "",
      intcmp: intcmp ? intcmp : "",
      medium: medium ? medium : "",
      cid: cid ? cid : "",
      campaigntype: campaigntype ? campaigntype : "",
      channelParam: checkParamValidity(
        channelParam,
        definePatternByName("ChannelParam")
      ),
      videoIdParam: videoIdParam ? videoIdParam : "",
      wave: wave ? wave : "",
      campaignCode: campaignCode ? campaignCode : "",
    };
    reduxDispatch(setQueryParams(queryParams));

    //Set class to html if in webview
    if (
      store.getState().queryParams.acquisitionpage === "iCornerApp" ||
      store.getState().queryParams.acquisitionpage === "Card24"
    )
      document.documentElement.classList.add("iCornerWebView");

    //Check if a lang is passed, and set it if valid
    if (langParam && ["it", "en", "fr", "de"].includes(langParam)) {
      i18n.changeLanguage(langParam);
    }

    // Run axios response interceptor
    startAxiosResponseInterceptor();

    // Load configuration
    await loadConfiguration(flow, acquisitionpage);

    if (videoIdParam === "success") {
      customNavigation("/SuccessVideoId");
      setTimeout(() => {
        setAppReady(true);
      }, 600);
    } else if (videoIdParam === "failure") {
      customNavigation("/FailureVideoId");
      setTimeout(() => {
        setAppReady(true);
      }, 600);
    } else {
      //Startup
      await appStartup(
        resumeToken,
        crossSellingParameter,
        checkParamValidity(channelParam, definePatternByName("ChannelParam"))
      );

      //Render app content
      setAppReady(true);
      reduxDispatch(setFader({ fading: false }));

      let crossSelling = store.getState().content.crossSelling;
      crossSelling ? setIsOnboardingLight(true) : setIsOnboardingLight(false);

      if (store.getState().pega?.Data?.Bank?.Customer?.KYC)
        setPagesWithBack(CrossSellingPagesWithBackButton);

      //Activate prompt to warn user when leaving the page
      window.onbeforeunload = () => true;
    }
  }, [
    searchParams,
    reduxDispatch,
    startAxiosResponseInterceptor,
    loadConfiguration,
    i18n,
    customNavigation,
    appStartup,
  ]);

  useEffect(() => {
    configurationAndStartup();
  }, []);

  const changeLanguage = (lang: "en" | "de" | "fr" | "it") => {
    i18n.changeLanguage(lang);
  };

  const stepProgressBarFull = pagesWithProgressBarFull.findIndex(
    (el) => el.page === window.document.location.pathname
  );
  const stepProgressBarLight = pagesWithProgressBarLight.findIndex(
    (el) => el.page === window.document.location.pathname
  );

  const buildProgressBarFull = () => {
    return stepProgressBarFull >= 0
      ? {
          step: pagesWithProgressBarFull[stepProgressBarFull].step,
          totalSteps: OnboardingFullSteps,
          customCssClass:
            store.getState().queryParams.acquisitionpage === "iCornerApp" ||
            store.getState().queryParams.acquisitionpage === "Card24"
              ? style.iCornerProgressBar
              : style.customProgressBar,
        }
      : undefined;
  };

  const buildProgressBarLight = () => {
    return stepProgressBarLight >= 0
      ? {
          step: pagesWithProgressBarLight[stepProgressBarLight].step,
          totalSteps: OnboardingLightSteps,
          customCssClass:
            store.getState().queryParams.acquisitionpage === "iCornerApp" ||
            store.getState().queryParams.acquisitionpage === "Card24"
              ? style.iCornerProgressBar
              : "",
        }
      : undefined;
  };

  const pageBack = async () => {
    let body: Request = {
      Data: {
        FlowDirection: "Back",
      },
      Extra: {},
    };

    setIsDisableBackButton(true);
    await callSteploader(body);
    setTimeout(() => setIsDisableBackButton(false), 600);
  };

  const cbHeader = (
    <Header
      logo={{ src: desktopLogo, mobileSrc: mobileLogo }}
      backButton={
        pagesWithBack.includes(window.document.location.pathname)
          ? {
              text:
                width > 768 || !isDisableBackButton
                  ? t`screens.common.back`
                  : "",
              onClick() {
                !isDisableBackButton && pageBack();
              },
              icon: isDisableBackButton
                ? { icon: "loader", position: "right", spin: true }
                : undefined,
            }
          : undefined
      }
      progressBar={
        isOnboardingLight ? buildProgressBarLight() : buildProgressBarFull()
      }
      customCssClass={style.customHeader}
    />
  );

  const cbHeaderMinified = (
    <Header
      customCssClass={`${style.minifiedHeader} ${
        isDisableBackButton ? style.disableBackButton : ""
      }`}
      minified={true}
      logo={{ src: "", mobileSrc: "" }}
      backButton={
        pagesWithBack.includes(window.document.location.pathname)
          ? {
              text:
                width > 768 || !isDisableBackButton
                  ? t`screens.common.back`
                  : "",
              onClick() {
                !isDisableBackButton && pageBack();
              },
              icon: isDisableBackButton
                ? { icon: "loader", position: "right", spin: true }
                : undefined,
            }
          : undefined
      }
      progressBar={
        isOnboardingLight ? buildProgressBarLight() : buildProgressBarFull()
      }
    />
  );

  const cbFooter = (
    <Footer
      text={{
        firstRow:
          `© ${new Date().getFullYear()} ` + t`components.footer.firstRow`,
        secondRow: t`components.footer.secondRow` || "",
      }}
      links={[
        { text: "EN", onLinkClick: () => changeLanguage("en") },
        { text: "DE", onLinkClick: () => changeLanguage("de") },
        { text: "FR", onLinkClick: () => changeLanguage("fr") },
        { text: "IT", onLinkClick: () => changeLanguage("it") },
        {
          text: t`screens.common.disclaimer`,
          href:
            store.getState().appConf.environment === "test" ||
            store.getState().appConf.environment === "prep"
              ? t`screens.common.disclaimerLink_prep`
              : t`screens.common.disclaimerLink_prod`,
          target: "_blank",
        },
      ]}
    />
  );

  const appContent = (
    <>
      <div id="fader-overlay"></div>
      {!appConf.workflowEnabled && <ManualNavigation />}
      <Routes>
        <Route>
          <Route path="/" element={<Navigate to="PersonalData" replace />} />
          <Route path="PersonalData" element={<PersonalData />} />
          <Route
            path="PersonalDataAdditional"
            element={<PersonalDataAdditional />}
          />
          <Route path="Address" element={<Address />} />
          <Route path="EmploymentDetails" element={<EmploymentDetails />} />
          <Route path="InformationRecap" element={<InformationRecap />} />
          <Route path="ContractView" element={<ContractView />} />
          <Route path="VideoIdentification" element={<VideoIdentification />} />
          <Route path="SuccessVideoId" element={<SuccessVideoId />} />
          <Route path="FailureVideoId" element={<FailureVideoId />} />
          <Route
            path="VideoIdentificationUnavailable"
            element={<VideoIdentificationUnavailable />}
          />
          <Route path="MissingData" element={<MissingData />} />
          <Route path="*" element={<>No matching route</>} />
        </Route>
        <Route path="GenericError" element={<GenericError />} />
      </Routes>
    </>
  );

  return (
    <>
      {store.getState().queryParams.acquisitionpage === "iCornerApp" ||
      store.getState().queryParams.acquisitionpage === "Card24"
        ? cbHeaderMinified
        : cbHeader}
      <main className={isFaded ? "main faded" : "main"} id="main-content">
        <ToastContainer></ToastContainer>
        {appReady && appContent}
      </main>
      {cbFooter}
    </>
  );
}

export default App;
